/* =============================================================================
 *  AndreiTasksCard — management surface for Andrei's autonomous-admin
 *  wake-actions (reminders / email watches / no-response chases / condition
 *  watches). Phase 1: notify-only (Andrei posts a message in your chat when a
 *  task fires).
 *
 *  Tasks are CREATED conversationally (ask Andrei: "remind me Friday to call
 *  Acme", "tell me when Acme emails", "chase Acme if no reply by next week",
 *  "flag any overdue invoice") via the scheduleTask tool. This card is the
 *  management half: list / pause / resume / run-now / cancel.
 *
 *  Data flow: list + all mutations go through the owner-gated manageAndreiTasks /
 *  runAndreiTaskNow endpoints (andrei_tasks is backend-write-only). Refetches on
 *  mount + after each action.
 * ============================================================================= */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useLocation } from 'react-router-dom';
import { useAndreiChat } from '../../../contexts/AndreiChatContext';
import {
  AndreiTaskRow, listAndreiTasks, cancelAndreiTask, setAndreiTaskPaused,
  deleteAndreiTask, runAndreiTaskNow,
} from '../andreiTasksApi';

function relTime(iso: string | null): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (!ms) return '';
  const diffMin = Math.round((ms - Date.now()) / 60000);
  const abs = Math.abs(diffMin);
  const suffix = diffMin >= 0 ? 'from now' : 'ago';
  if (abs < 60) return `${abs}m ${suffix}`;
  const hr = Math.round(abs / 60);
  if (hr < 24) return `${hr}h ${suffix}`;
  return `${Math.round(hr / 24)}d ${suffix}`;
}

const AndreiTasksCard: React.FC = () => {
  const { openAndreiChat } = useAndreiChat();
  const location = useLocation();
  const [tasks, setTasks] = useState<AndreiTaskRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await listAndreiTasks(false);
      if (!r.ok) throw new Error(r.error || 'failed');
      setError(null);
      setTasks(r.tasks || []);
    } catch (e: any) {
      setError(e?.message || 'unavailable');
      setTasks([]);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (location.hash === '#andrei-tasks' && tasks) document.getElementById('andrei-tasks')?.scrollIntoView?.({ block: 'start' });
  }, [location.hash, tasks]);

  const clearBusy = useCallback((id: string) => setBusyId((cur) => (cur === id ? null : cur)), []);

  const togglePause = useCallback(async (t: AndreiTaskRow) => {
    setBusyId(t.id);
    try {
      const paused = t.status !== 'paused';
      const r = await setAndreiTaskPaused(t.id, paused);
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success(paused ? 'Reminder paused.' : 'Reminder resumed.');
      await refresh();
    } catch (e: any) {
      toast.error(`Couldn't update: ${e?.message || 'error'}`);
    } finally { clearBusy(t.id); }
  }, [clearBusy, refresh]);

  const runNow = useCallback(async (t: AndreiTaskRow) => {
    setBusyId(t.id);
    try {
      const r = await runAndreiTaskNow(t.id);
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success(`Ran "${t.title}": ${r.outcome || 'done'}.`);
      await refresh();
    } catch (e: any) {
      toast.error(`Run failed: ${e?.message || 'error'}`);
    } finally { clearBusy(t.id); }
  }, [clearBusy, refresh]);

  const cancel = useCallback(async (t: AndreiTaskRow) => {
    setBusyId(t.id);
    setConfirmCancelId(null);
    try {
      const r = await cancelAndreiTask(t.id);
      if (!r.ok) throw new Error(r.error || 'failed');
      // Also delete so it drops off the active list cleanly.
      await deleteAndreiTask(t.id).catch(() => {});
      toast.success('Reminder cancelled.');
      await refresh();
    } catch (e: any) {
      toast.error(`Couldn't cancel: ${e?.message || 'error'}`);
    } finally { clearBusy(t.id); }
  }, [clearBusy, refresh]);

  const activeCount = (tasks || []).filter((t) => t.status === 'scheduled' || t.status === 'running').length;

  return (
    <section id="andrei-tasks" className="home-monitors" aria-label="Andrei automations">
      <header className="home-monitors__head">
        <span className="home-monitors__title">Andrei tasks and reviews</span>
        {tasks && tasks.length > 0 && (
          <span className="home-monitors__count">{activeCount}/{tasks.length} active</span>
        )}
      </header>

      {error && <div className="home-monitors__note">reminders unavailable: {error}</div>}
      {!error && tasks === null && <div className="home-monitors__note">loading reminders…</div>}
      {!error && tasks !== null && tasks.length === 0 && (
        <div className="home-monitors__note">
          no reminders yet — ask Andrei, e.g. <em>“remind me Friday to call Acme”</em> or <em>“tell me if we don’t hear back from Acme by next week”</em>
        </div>
      )}

      {(tasks || []).map((t) => {
        const busy = busyId === t.id;
        const paused = t.status === 'paused';
        return (
          <div key={t.id} className={`home-monitors__row${paused ? ' home-monitors__row--paused' : ''}`}>
            <div className="home-monitors__row-head">
              <span className="home-monitors__acct" title={t.title}>{t.title}</span>
              <span className="home-monitors__cadence">{t.mode}</span>
              <span className={`home-monitors__state home-monitors__state--${paused || t.status === 'error' ? 'off' : 'on'}`}>
                {t.supportReview && t.status === 'error' ? 'needs checking' : paused ? 'paused' : t.status}
              </span>
            </div>
            <div className="home-monitors__watch">
              <span className="home-monitors__chip">{t.trigger}</span>
              {t.nextRunAt && <span className="home-monitors__chip home-monitors__chip--surface">next {relTime(t.nextRunAt)}</span>}
            </div>
            {(t.instruction || t.lastOutcome) && !t.supportReview && (
              <div className="home-monitors__digest" title={t.instruction || ''}>
                {t.instruction || ''}
                {t.lastRunAt && <span className="home-monitors__when"> · last {relTime(t.lastRunAt)} ({t.lastOutcome || '—'})</span>}
              </div>
            )}
            {t.supportReview && <div className="home-monitors__note">Monthly support · {t.supportReview.month} · {t.supportReview.kind === 'generation_error' ? 'Work preparation needs review' : t.supportReview.kind === 'integrity' ? 'Service records need review' : t.supportReview.kind === 'overdue' ? 'Overdue service' : 'Outstanding service'}{t.lastOutcome ? ` · ${t.lastOutcome}` : ''}</div>}
            {t.supportReview && t.lastError && <div className="home-monitors__note" role="alert">{t.lastError}</div>}
            <div className="home-monitors__actions">
              {t.supportReview ? <>
                <Link className="home-monitors__btn" to={`/monthly-support?month=${encodeURIComponent(t.supportReview.month)}${t.customerId ? `&id=${encodeURIComponent(t.customerId)}` : ''}`}>Open monthly support ↗</Link>
                {t.sessionId && (t.lastRunAt || t.status === 'error') && <button type="button" className="home-monitors__btn" onClick={() => openAndreiChat({ sessionId: t.sessionId, context: t.customerId ? { type: 'customer', id: t.customerId } : null })}>Open review conversation</button>}
              </> : <>
              <button type="button" className="home-monitors__btn" disabled={busy} onClick={() => runNow(t)} title="Fire this reminder now">
                {busy ? '…' : '[run now]'}
              </button>
              <button type="button" className="home-monitors__btn" disabled={busy} onClick={() => togglePause(t)}>
                {paused ? '[resume]' : '[pause]'}
              </button>
              {confirmCancelId === t.id ? (
                <>
                  <button type="button" className="home-monitors__btn home-monitors__btn--danger" disabled={busy} onClick={() => cancel(t)}>[confirm]</button>
                  <button type="button" className="home-monitors__btn" disabled={busy} onClick={() => setConfirmCancelId(null)}>[keep]</button>
                </>
              ) : (
                <button type="button" className="home-monitors__btn home-monitors__btn--danger" disabled={busy} onClick={() => setConfirmCancelId(t.id)}>[cancel]</button>
              )}
              </>}
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default AndreiTasksCard;
